import { Component, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Top-level error boundary. Without this, any uncaught render error
 * crashes the whole SPA to a blank #root div with no feedback. The
 * boundary catches errors at render time + lifecycle methods, shows
 * a friendly fallback, and lets the user retry (hard reload) or go
 * home.
 *
 * React Hooks (useEffect / event handlers) errors are NOT caught
 * by error boundaries — those bubble through window.onerror, which
 * we don't currently hook. For a v1 platform this is acceptable;
 * the boundary handles the common render-crash path.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string }): void {
    // Log to console in case the user's DevTools is open — operators
    // can ask them to paste the stack. CloudWatch RUM / Sentry-style
    // shipping is a separate change.
    console.error('[fan-error-boundary]', error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50 px-3 sm:px-4 py-6">
          <div
            role="alert"
            className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 max-w-md w-full space-y-4"
          >
            {/* Big icon to mirror Confirm/SignInCallback's outcome
                circles — keeps the error-state visual language
                consistent across the SPA. */}
            <div className="flex flex-col items-center text-center">
              <div className="h-16 w-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mb-3 text-rose-300 text-3xl">
                ✕
              </div>
              <h1 className="text-xl sm:text-2xl font-semibold">頁面發生錯誤</h1>
            </div>
            <p className="text-slate-400 text-sm text-center">
              我們的程式跑到了預期外的狀況，這個頁面沒辦法繼續顯示。重新整理或回到首頁通常能解決。
            </p>
            <details className="text-slate-500 text-xs">
              <summary className="cursor-pointer hover:text-slate-400">技術細節（給工程師看）</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words bg-slate-950 rounded p-2 max-h-40 overflow-auto">
                {this.state.error.message}
                {this.state.error.stack ? '\n\n' + this.state.error.stack.slice(0, 800) : ''}
              </pre>
            </details>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => { window.location.reload(); }}
                className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300"
              >重新整理</button>
              <button
                type="button"
                onClick={() => { window.location.assign('/'); }}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
              >回首頁</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
